import Dexie, { type EntityTable } from 'dexie';
import type { Ballot, FlowCell, FlowRow, LedgerEvent, Round, RoundNames, Settings, TimerRecord, Tournament } from '@flow/core';

/** A change made on this device, waiting to be sent. `n` orders the queue (the server's order is `seq`). */
export type OutboxEntry = LedgerEvent & { n?: number };
/** The event each record's live copy came from: newer incoming events replace it, older ones are skipped. */
export interface HeadEntry { key: string; at: number; id: string }
export interface MetaEntry { key: string; value: unknown }

/** One database per account: `flow-<account id>`. (`flow` is the pre-accounts database; see claimLegacy.) */
export const accountDbName = (accountId: string) => `flow-${accountId.replace(/[^A-Za-z0-9_-]/g, '_')}`;

export class FlowDb extends Dexie {
  tournaments!: EntityTable<Tournament, 'id'>;
  rounds!: EntityTable<Round, 'id'>;
  /** Device-local: debater names never leave this device. */
  names!: EntityTable<RoundNames, 'roundId'>;
  rows!: EntityTable<FlowRow, 'id'>;
  cells!: EntityTable<FlowCell, 'id'>;
  timers!: EntityTable<TimerRecord, 'roundId'>;
  ballots!: EntityTable<Ballot, 'roundId'>;
  settings!: EntityTable<Settings, 'id'>;
  // Sync (see features/sync): changes waiting to be sent, what each record's live copy came from, and state.
  outbox!: EntityTable<OutboxEntry, 'n'>;
  heads!: EntityTable<HeadEntry, 'key'>;
  meta!: EntityTable<MetaEntry, 'key'>;

  constructor(name: string) {
    super(name);
    this.version(1).stores({
      tournaments: 'id, updatedAt',
      rounds: 'id, tournamentId, updatedAt',
      names: 'roundId, purgeAfter',
      rows: 'id, roundId, sheetId',
      cells: 'id, roundId, rowId',
      timers: 'roundId',
      ballots: 'roundId',
      settings: 'id',
    });
    this.version(2).stores({ outbox: '++n', heads: 'key', meta: 'key' });
  }

  /** Every table, for transactions that must see all of them (use cases, applying synced changes). */
  get everything() {
    return [this.tournaments, this.rounds, this.names, this.rows, this.cells, this.timers, this.ballots, this.settings, this.outbox, this.heads, this.meta];
  }
}
